import type { ResultOf } from '@graphql-typed-document-node/core'
import { useCallback } from 'react'
import { twMerge } from 'tailwind-merge'
import type { RestaurantCalendar } from '~/features/calendar/RestaurantCalendar.frag'
import {
  dayOfWeekJP,
  getDayOfWeek,
  getIsHoliday,
  shouldShowPhoneNumber,
} from '~/lib/calendar/calendar'
import { useNow } from '~/lib/date/NowProvider'
import { addDays, format, getDate, getMonth } from '~/lib/date/date'
import type { FragmentType } from '~/lib/graphql'
import { getFragmentData, graphql } from '~/lib/graphql'
import type { DateText } from '~/lib/graphql/scalars'
import { useReserveNavigate } from '~/navigation'
import { css } from '~/styled-system/css'
import type { ReservableCalendarFragment } from '../reservableCalendar.frag'
import IsReservable from './IsReservable'

const RestaurantTwoWeeksCalendar = graphql(/* GraphQL */ `
  fragment RestaurantTwoWeeksCalendar on Restaurant {
    phoneNumber
  }
`)

type Props = {
  reservableCalendar: ResultOf<typeof ReservableCalendarFragment>
  restaurant: FragmentType<typeof RestaurantTwoWeeksCalendar> &
    FragmentType<typeof RestaurantCalendar>
}
const WEEK_DAYS_COUNT: number = 7

export function TwoWeeksInventoryCalendar({ reservableCalendar, restaurant }: Props) {
  const { phoneNumber } = getFragmentData(RestaurantTwoWeeksCalendar, restaurant)
  const now = useNow()
  const navigate = useReserveNavigate()

  const handleClickDay = useCallback(
    (visitDate: DateText, reservable: boolean, callAvailable: boolean) => {
      if (reservable) {
        navigate({
          type: 'openCalendarModal',
          visitDate,
          openCalendarInitially: false,
          isFiltered: false,
        })
        return
      }
      if (callAvailable) {
        const a = document.createElement('a')
        a.href = `tel:${phoneNumber}`
        a.click()
      }
    },
    [navigate, phoneNumber]
  )

  const handleClickMore = useCallback(
    (fromDate: DateText) => {
      navigate({
        type: 'openCalendarModal',
        fromDate,
        openCalendarInitially: true,
        isFiltered: false,
      })
    },
    [navigate]
  )

  // 2週間カレンダーでは先頭14日分を使う
  const twoWeeksReservableDates = reservableCalendar.dates.slice(0, 14)

  return (
    <>
      {/* 曜日を出力 */}
      <div className="mb-1 grid w-full grid-cols-7 text-sm">
        {twoWeeksReservableDates.slice(0, WEEK_DAYS_COUNT).map((reservableDate, index) => {
          const dayOfWeek = getDayOfWeek(reservableDate)
          return (
            <div
              key={index}
              className={twMerge(
                'text-center',
                dayOfWeek === 'SUN' && 'text-peach-dark',
                dayOfWeek === 'SAT' && 'text-blueberry-dark'
              )}
            >
              {dayOfWeekJP[dayOfWeek]}
            </div>
          )
        })}
      </div>
      {/* カレンダーの日付と在庫を出力 */}
      <div className="grid w-full grid-cols-7 gap-px border border-neutral-200 bg-neutral-200 text-sm font-bold leading-none">
        {twoWeeksReservableDates.map((reservableDate, index) => {
          const date = reservableDate.calendarDate.date
          const month = getMonth(date)
          const day = getDate(date)
          const dayOfWeek = getDayOfWeek(reservableDate)
          const isHoliday = getIsHoliday(reservableDate)
          const reservable = reservableDate.reservable
          const callAvailable =
            shouldShowPhoneNumber(date, reservableDate.hasClosedInventory, now) && !!phoneNumber

          return (
            <div
              key={index}
              className={twMerge(
                'flex h-11 flex-col items-center justify-center gap-y-1 bg-neutral-000 leading-none',
                dayOfWeek === 'SUN' || isHoliday ? ' text-peach-dark' : '',
                dayOfWeek === 'SAT' ? 'text-blueberry-dark' : ''
              )}
              onClick={() => handleClickDay(date, reservable, callAvailable)}
            >
              {/* カレンダーの初日と1日は月も表示する(月/日) */}
              {index === 0 || day === 1 ? month + '/' + day : day}
              <IsReservable reservable={reservable} callAvailable={callAvailable} />
            </div>
          )
        })}
      </div>
      <MoreDatesCalendarLink
        reservableDates={twoWeeksReservableDates}
        onClick={handleClickMore}
        reservableCalendar={reservableCalendar}
      />
    </>
  )
}

type FirstReservableDateProps = {
  reservableCalendar: ResultOf<typeof ReservableCalendarFragment>
}

function FirstReservableDate({ reservableCalendar }: FirstReservableDateProps) {
  const firstReservableDate = reservableCalendar.dates.find((date) => date.reservable)?.calendarDate
    .date
  if (firstReservableDate === undefined) {
    return null
  }
  return (
    <div className="relative mb-2 inline-flex items-center justify-center whitespace-nowrap rounded bg-marron-pale px-2 py-1 leading-none text-neutral-000 before:absolute before:left-1/2 before:top-full before:-translate-x-1/2 before:border-x-6 before:border-b-0 before:border-t-6 before:border-t-marron-pale before:content-['']">
      <p className="text-sm font-bold text-marron-dark">
        最短で<span className="mx-1">{format(firstReservableDate, 'M/d(E)')}</span>に空席があります
      </p>
    </div>
  )
}

type MoreDatesCalendarLinkProps = {
  reservableDates: ResultOf<typeof ReservableCalendarFragment>['dates']
  onClick: (date: DateText) => void
  reservableCalendar: ResultOf<typeof ReservableCalendarFragment>
}

function MoreDatesCalendarLink({
  reservableDates,
  onClick,
  reservableCalendar,
}: MoreDatesCalendarLinkProps) {
  // 最終日の翌日
  const date = addDays(reservableDates[reservableDates.length - 1].calendarDate.date, 1)

  return (
    <button
      type="button"
      className={css({ width: 'full', py: '3', fontSize: 'md' })}
      onClick={() => onClick(date)}
    >
      {reservableDates.every((date) => !date.reservable) && (
        <FirstReservableDate reservableCalendar={reservableCalendar} />
      )}
      <p className={css({ color: 'link' })}>{format(date, 'M/d(E)')} 以降を予約する</p>
    </button>
  )
}
